// src/pages/admin/AdminCycles.tsx
//
// Cycles & Quarters management page.
//
// ACCESS MAP (mirrors backend requireAdminOrChair middleware):
//   admin            — full CRUD: create, edit, activate, lock, complete
//   committee_chair  — same as admin
//   committee_member — read-only (no create / edit / status change)
//   portal_manager   — read-only
//   regional_rep     — no access (router should redirect, but gracefully handled)
//
// WHAT THIS PAGE COVERS:
//   • 4-quarter grid showing each cycle's current status, theme, dates, progress
//   • "Seed defaults" one-click — scaffolds all 4 Q1-Q4 cycles for a given year
//   • Create cycle form (slide-in panel) with all 90-day milestone dates
//   • Edit cycle form (reuses same panel)
//   • Status transitions: locked → upcoming → active → completed
//     (enforces single-active-at-a-time via backend)
//   • Days-to-event countdown and progress bar for the active cycle

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Plus,
  Calendar,
  Check,
  X,
  AlertCircle,
  Loader2,
  Flame,
  Lock,
  PlayCircle,
  CheckCircle2,
  Clock,
  Edit3,
  Zap,
  Info,
} from "lucide-react";

import { useAppSelector }                        from "../../app/hooks";
import {
  useGetAllCyclesQuery,
  useCreateCycleMutation,
  useUpdateCycleMutation,
}                                                from "../../app/api/programmeApi";
import {
  useSeedDefaultCyclesMutation,
}                                                from "../../app/api/adminApi";
import type { ProgrammeCycle, QuarterKey, QuarterStatus } from "../../app/api/programmeApi";
import type { UserRole }                         from "../../app/api/authApi";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const CAN_WRITE: UserRole[] = ["admin", "committee_chair"];

const QUARTER_THEMES: Record<QuarterKey, { title: string; tagline: string }> = {
  Q1: { title: "Foundations of Ministry & Grace",  tagline: "Roots of the Vision"       },
  Q2: { title: "Strategic Governance & Ethics",    tagline: "Structure & Integrity"      },
  Q3: { title: "Marketplace Dominance",            tagline: "Pulpit Meets Profession"    },
  Q4: { title: "Legacy & Succession",              tagline: "Sustaining the Move of God" },
};

const QUARTER_ORDER: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

const STATUS_META: Record<QuarterStatus, {
  label:     string;
  colour:    string;
  icon:      React.ReactNode;
  dot:       string;
}> = {
  locked:    { label: "Locked",    colour: "bg-gray-100   text-gray-500",   icon: <Lock        size={12} />, dot: "bg-gray-400"   },
  upcoming:  { label: "Upcoming",  colour: "bg-blue-100   text-blue-700",   icon: <Clock       size={12} />, dot: "bg-blue-400"   },
  active:    { label: "Active",    colour: "bg-green-100  text-green-700",  icon: <PlayCircle  size={12} />, dot: "bg-green-500"  },
  completed: { label: "Completed", colour: "bg-purple-100 text-purple-700", icon: <CheckCircle2 size={12} />, dot: "bg-purple-400" },
};

// Valid status transitions an admin can make
const NEXT_STATUSES: Record<QuarterStatus, QuarterStatus[]> = {
  locked:    ["upcoming"],
  upcoming:  ["active", "locked"],
  active:    ["completed"],
  completed: [],
};

/* ─────────────────────────────────────────────────────────────────
   FORM STATE
───────────────────────────────────────────────────────────────── */

interface CycleFormProps {
  year:                      string;
  quarter:                   QuarterKey;
  theme:                     string;
  tagline:                   string;
  status:                    QuarterStatus;
  pillars:                   string;          // comma-separated
  eventDate:                 string;
  taskSubmissionDeadline:    string;
  fellowsSelectionDeadline:  string;
  needsAssessmentStart:      string;
  needsAssessmentEnd:        string;
  contentDevStart:            string;
  contentDevEnd:              string;
  mobilisationStart:         string;
  mobilisationEnd:           string;
  shadowingWeekStart:        string;
  shadowingWeekEnd:          string;
}

const EMPTY_FORM: CycleFormProps = {
  year:                      String(new Date().getFullYear()),
  quarter:                   "Q1",
  theme:                     "",
  tagline:                   "",
  status:                    "locked",
  pillars:                   "",
  eventDate:                 "",
  taskSubmissionDeadline:    "",
  fellowsSelectionDeadline:  "",
  needsAssessmentStart:      "",
  needsAssessmentEnd:        "",
  contentDevStart:            "",
  contentDevEnd:              "",
  mobilisationStart:         "",
  mobilisationEnd:           "",
  shadowingWeekStart:        "",
  shadowingWeekEnd:          "",
};

const cycleToForm = (c: ProgrammeCycle): CycleFormProps => ({
  year:                      String(c.year ?? new Date().getFullYear()),
  quarter:                   c.quarter,
  theme:                     c.theme  ?? "",
  tagline:                   c.tagline ?? "",
  status:                    c.status,
  pillars:                   c.pillars?.join(", ") ?? "",
  eventDate:                 c.eventDate           ? c.eventDate.slice(0, 10)                : "",
  taskSubmissionDeadline:    c.taskSubmissionDeadline   ? c.taskSubmissionDeadline.slice(0, 10)   : "",
  fellowsSelectionDeadline:  c.fellowsSelectionDeadline ? c.fellowsSelectionDeadline.slice(0, 10) : "",
  needsAssessmentStart:      (c as any).needsAssessmentStart ? (c as any).needsAssessmentStart.slice(0, 10) : "",
  needsAssessmentEnd:        (c as any).needsAssessmentEnd   ? (c as any).needsAssessmentEnd.slice(0, 10)   : "",
  contentDevStart:            (c as any).contentDevStart      ? (c as any).contentDevStart.slice(0, 10)      : "",
  contentDevEnd:              (c as any).contentDevEnd        ? (c as any).contentDevEnd.slice(0, 10)        : "",
  mobilisationStart:         (c as any).mobilisationStart    ? (c as any).mobilisationStart.slice(0, 10)    : "",
  mobilisationEnd:           (c as any).mobilisationEnd      ? (c as any).mobilisationEnd.slice(0, 10)      : "",
  shadowingWeekStart:        c.shadowingWeekStart ? c.shadowingWeekStart.slice(0, 10) : "",
  shadowingWeekEnd:          c.shadowingWeekEnd   ? c.shadowingWeekEnd.slice(0, 10)   : "",
});

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const FL: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (p) => (
  <input
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition disabled:bg-gray-50 disabled:text-gray-400 ${p.className ?? ""}`}
  />
);

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (p) => (
  <select
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      transition disabled:bg-gray-50 ${p.className ?? ""}`}
  />
);

const SLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-2">{children}</p>
);

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

/* ─────────────────────────────────────────────────────────────────
   CYCLE CARD
───────────────────────────────────────────────────────────────── */

const CycleCard: React.FC<{
  quarter:    QuarterKey;
  cycle:      ProgrammeCycle | null;
  canWrite:   boolean;
  onEdit:     (c: ProgrammeCycle) => void;
  onCreate:   (q: QuarterKey) => void;
  onStatus:   (c: ProgrammeCycle, s: QuarterStatus) => void;
  isUpdating: boolean;
}> = ({ quarter, cycle, canWrite, onEdit, onCreate, onStatus, isUpdating }) => {
  const theme   = QUARTER_THEMES[quarter];
  const sm      = cycle ? STATUS_META[cycle.status] : null;
  const nexts   = cycle ? NEXT_STATUSES[cycle.status] : [];

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col
      ${cycle?.status === "active" ? "ring-2 ring-purple-500 border-transparent" : "border-gray-100"}`}>

      {/* Quarter stripe */}
      <div className={`h-1.5 w-full ${
        !cycle              ? "bg-gray-100" :
        cycle.status === "active"    ? "bg-purple-500" :
        cycle.status === "completed" ? "bg-green-400"  :
        cycle.status === "upcoming"  ? "bg-blue-400"   :
                                       "bg-gray-200"
      }`} />

      <div className="p-5 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md">{quarter}</span>
              {sm && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${sm.colour}`}>
                  {sm.icon}{sm.label}
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 leading-snug">
              {cycle?.theme ?? theme.title}
            </h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {cycle?.tagline ?? theme.tagline}
            </p>
          </div>
          {canWrite && cycle && (
            <button
              onClick={() => onEdit(cycle)}
              className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              title="Edit cycle"
            >
              <Edit3 size={14} />
            </button>
          )}
        </div>

        {/* Dates */}
        {cycle ? (
          <div className="space-y-1.5 mb-4 flex-1">
            {cycle.eventDate && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-400 flex items-center gap-1"><Calendar size={10} /> Event date</span>
                <span className="font-semibold text-gray-700">{fmtDate(cycle.eventDate)}</span>
              </div>
            )}
            {cycle.taskSubmissionDeadline && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-400">Task deadline</span>
                <span className="font-medium text-gray-600">{fmtDate(cycle.taskSubmissionDeadline)}</span>
              </div>
            )}
            {cycle.fellowsSelectionDeadline && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-400">Fellows deadline</span>
                <span className="font-medium text-gray-600">{fmtDate(cycle.fellowsSelectionDeadline)}</span>
              </div>
            )}
            {/* Pillars */}
            {cycle.pillars?.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {cycle.pillars.map((p) => (
                  <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{p}</span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-6 text-center">
            <div>
              <div className="w-10 h-10 rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center mx-auto mb-2">
                <Plus size={16} className="text-gray-300" />
              </div>
              <p className="text-[11px] text-gray-400">No cycle created yet</p>
            </div>
          </div>
        )}

        {/* Progress bar — active cycle only */}
        {cycle?.status === "active" && (
          <div className="mb-4">
            <div className="flex justify-between text-[11px] text-gray-400 mb-1">
              <span>Quarter progress</span>
              <span className="font-semibold text-purple-700">{cycle.progressPercent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-purple-500 transition-all duration-700"
                style={{ width: `${cycle.progressPercent}%` }}
              />
            </div>
            {cycle.daysToEvent !== null && (
              <p className="text-[11px] text-purple-600 font-medium mt-1">
                {cycle.daysToEvent} day{cycle.daysToEvent !== 1 ? "s" : ""} to event
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        {canWrite && (
          <div className="pt-3 border-t border-gray-50 space-y-2">
            {!cycle ? (
              <button
                onClick={() => onCreate(quarter)}
                className="w-full text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-purple-200 hover:bg-purple-50 transition-colors"
              >
                <Plus size={13} /> Create {quarter} cycle
              </button>
            ) : (
              <>
                {nexts.map((next) => {
                  const nextMeta = STATUS_META[next];
                  return (
                    <button
                      key={next}
                      onClick={() => onStatus(cycle, next)}
                      disabled={isUpdating}
                      className={`w-full text-xs font-semibold flex items-center justify-center gap-1.5 py-2 rounded-lg transition-colors disabled:opacity-50
                        ${next === "active"
                          ? "bg-purple-600 text-white hover:bg-purple-700"
                          : next === "completed"
                            ? "bg-green-600 text-white hover:bg-green-700"
                            : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {isUpdating ? <Loader2 size={12} className="animate-spin" /> : nextMeta.icon}
                      Mark as {nextMeta.label}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   CYCLE FORM
───────────────────────────────────────────────────────────────── */

const CycleForm: React.FC<{
  initial:   CycleFormProps;
  isEditing: boolean;
  isSaving:  boolean;
  apiError:  string | null;
  onSave:    (f: CycleFormProps) => void;
  onCancel:  () => void;
}> = ({ initial, isEditing, isSaving, apiError, onSave, onCancel }) => {
  const [form,   setForm]   = useState<CycleFormProps>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { setForm(initial); setErrors({}); }, [initial]);

  const set = useCallback(
    <K extends keyof CycleFormProps>(k: K, v: CycleFormProps[K]) =>
      setForm((p) => ({ ...p, [k]: v })),
    [],
  );

  // Auto-fill theme when quarter changes (new cycles only)
  const handleQuarterChange = (q: QuarterKey) => {
    set("quarter", q);
    if (!isEditing) {
      set("theme",  QUARTER_THEMES[q].title);
      set("tagline", QUARTER_THEMES[q].tagline);
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.year || isNaN(Number(form.year))) e.year    = "Valid year is required.";
    if (!form.quarter)                           e.quarter = "Quarter is required.";
    if (!form.theme.trim())                      e.theme   = "Theme is required.";
    if (!form.eventDate)                         e.eventDate = "Event date is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  const D = ({ label, field, required }: { label: string; field: keyof CycleFormProps; required?: boolean }) => (
    <div>
      <FL required={required}>{label}</FL>
      <Input
        type="date"
        value={form[field] as string}
        onChange={(e) => set(field, e.target.value)}
      />
      {errors[field] && <p className="text-[11px] text-red-500 mt-1">{errors[field]}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Year + Quarter */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FL required>Year</FL>
          <Input
            type="number"
            min={2024}
            max={2099}
            placeholder={String(new Date().getFullYear())}
            value={form.year}
            onChange={(e) => set("year", e.target.value)}
            disabled={isEditing}
          />
          {errors.year && <p className="text-[11px] text-red-500 mt-1">{errors.year}</p>}
        </div>
        <div>
          <FL required>Quarter</FL>
          <Sel
            value={form.quarter}
            onChange={(e) => handleQuarterChange(e.target.value as QuarterKey)}
            disabled={isEditing}
          >
            {QUARTER_ORDER.map((q) => <option key={q} value={q}>{q}</option>)}
          </Sel>
          {errors.quarter && <p className="text-[11px] text-red-500 mt-1">{errors.quarter}</p>}
        </div>
      </div>

      {/* Theme */}
      <div>
        <FL required>Theme</FL>
        <Input placeholder="e.g. Foundations of Ministry & Grace" value={form.theme} onChange={(e) => set("theme", e.target.value)} maxLength={200} />
        {errors.theme && <p className="text-[11px] text-red-500 mt-1">{errors.theme}</p>}
      </div>

      {/* Tagline */}
      <div>
        <FL>Tagline</FL>
        <Input placeholder="e.g. Roots of the Vision" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} maxLength={200} />
      </div>

      {/* Status */}
      <div>
        <FL>Status</FL>
        <Sel value={form.status} onChange={(e) => set("status", e.target.value as QuarterStatus)}>
          {(["locked", "upcoming", "active", "completed"] as QuarterStatus[]).map((s) => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </Sel>
        {form.status === "active" && (
          <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
            <AlertCircle size={10} /> Only one cycle can be globally active at a time.
          </p>
        )}
      </div>

      {/* Pillars */}
      <div>
        <FL>Programme pillars</FL>
        <Input placeholder="Governance, Ethics, Character, Competence  (comma-separated)" value={form.pillars} onChange={(e) => set("pillars", e.target.value)} />
      </div>

      {/* Core dates */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
        <SLabel>Core dates</SLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <D label="Event date (Day 90)" field="eventDate" required />
          <D label="Task submission deadline"    field="taskSubmissionDeadline"   />
          <D label="Fellows selection deadline"  field="fellowsSelectionDeadline" />
        </div>
      </div>

      {/* 90-day milestones */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
        <SLabel>90-day milestones (optional)</SLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <D label="Needs assessment start"  field="needsAssessmentStart" />
          <D label="Needs assessment end"    field="needsAssessmentEnd"   />
          <D label="Content dev start"       field="contentDevStart"       />
          <D label="Content dev end"         field="contentDevEnd"         />
          <D label="Mobilisation start"      field="mobilisationStart"    />
          <D label="Mobilisation end"        field="mobilisationEnd"      />
          <D label="Shadowing week start"    field="shadowingWeekStart"   />
          <D label="Shadowing week end"      field="shadowingWeekEnd"     />
        </div>
      </div>

      {apiError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
          <AlertCircle size={13} className="shrink-0" />{apiError}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all shadow-sm disabled:opacity-50"
        >
          {isSaving
            ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
            : <><Check size={14} /> {isEditing ? "Save changes" : "Create cycle"}</>
          }
        </button>
      </div>
    </form>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SEED DEFAULTS MODAL
───────────────────────────────────────────────────────────────── */

const SeedModal: React.FC<{
  onConfirm: (year: number) => void;
  isLoading: boolean;
  onCancel:  () => void;
}> = ({ onConfirm, isLoading, onCancel }) => {
  const [year, setYear] = useState(String(new Date().getFullYear()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
            <Zap size={18} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Seed default cycles</h3>
            <p className="text-xs text-gray-500">Creates Q1–Q4 for the selected year using built-in themes.</p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2 text-[11px] text-gray-500">
          {QUARTER_ORDER.map((q) => (
            <div key={q} className="flex items-center gap-2">
              <span className="font-bold text-purple-700 w-6">{q}</span>
              <span>{QUARTER_THEMES[q].title}</span>
            </div>
          ))}
          <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1">
            <Info size={10} className="shrink-0" /> Quarters that already exist for this year will be skipped.
          </p>
        </div>
        <div className="mb-4">
          <FL required>Year</FL>
          <Input type="number" min={2024} max={2099} value={year} onChange={(e) => setYear(e.target.value)} />
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
          <button
            onClick={() => onConfirm(Number(year))}
            disabled={isLoading || !year}
            className="flex-1 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? "Seeding…" : "Seed cycles"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const AdminCycles: React.FC = () => {
  const user     = useAppSelector((s) => s.auth.user)!;
  const role     = (user.role ?? "committee_member") as UserRole;
  const canWrite = CAN_WRITE.includes(role);

  const [showForm,    setShowForm]    = useState(false);
  const [showSeed,    setShowSeed]    = useState(false);
  const [editTarget,  setEditTarget]  = useState<ProgrammeCycle | null>(null);
  const [defaultQ,    setDefaultQ]    = useState<QuarterKey>("Q1");
  const [formError,   setFormError]   = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const { data: cyclesRes, isLoading } = useGetAllCyclesQuery();
  const [createCycle, { isLoading: isCreating }] = useCreateCycleMutation();
  const [updateCycle, { isLoading: isUpdating }] = useUpdateCycleMutation();
  const [seedCycles,  { isLoading: isSeeding  }] = useSeedDefaultCyclesMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cycles = cyclesRes?.data ?? [];

  // Build a map of quarter → most recent cycle
  const cycleMap = useMemo<Partial<Record<QuarterKey, ProgrammeCycle>>>(() => {
    const m: Partial<Record<QuarterKey, ProgrammeCycle>> = {};
    for (const c of cycles) {
      const existing = m[c.quarter];
      if (!existing || (c.year ?? 0) > (existing.year ?? 0)) m[c.quarter] = c;
    }
    return m;
  }, [cycles]);

  const activeCycle = cycles.find((c) => c.status === "active");

  const openCreate = (q: QuarterKey) => {
    // const preForm = { ...EMPTY_FORM, quarter: q, theme: QUARTER_THEMES[q].title, tagline: QUARTER_THEMES[q].tagline };
    setDefaultQ(q);
    setEditTarget(null);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c: ProgrammeCycle) => {
    setEditTarget(c);
    setFormError(null);
    setShowForm(true);
  };

  const handleSave = async (form: CycleFormProps) => {
    setFormError(null);
    try {
      const payload = {
        year:                      Number(form.year),
        quarter:                   form.quarter,
        theme:                     form.theme.trim(),
        tagline:                   form.tagline.trim() || undefined,
        status:                    form.status,
        pillars:                   form.pillars ? form.pillars.split(",").map((p) => p.trim()).filter(Boolean) : [],
        eventDate:                 form.eventDate,
        taskSubmissionDeadline:    form.taskSubmissionDeadline    || undefined,
        fellowsSelectionDeadline:  form.fellowsSelectionDeadline  || undefined,
        needsAssessmentStart:      form.needsAssessmentStart      || undefined,
        needsAssessmentEnd:        form.needsAssessmentEnd        || undefined,
        contentDevStart:            form.contentDevStart            || undefined,
        contentDevEnd:              form.contentDevEnd              || undefined,
        mobilisationStart:         form.mobilisationStart         || undefined,
        mobilisationEnd:           form.mobilisationEnd           || undefined,
        shadowingWeekStart:        form.shadowingWeekStart        || undefined,
        shadowingWeekEnd:          form.shadowingWeekEnd          || undefined,
      };

      if (editTarget) {
        await updateCycle({ id: editTarget.id as string, ...payload }).unwrap();
      } else {
        await createCycle(payload).unwrap();
      }
      setShowForm(false);
      setEditTarget(null);
    } catch (err: any) {
      setFormError(err?.data?.error ?? "Something went wrong. Please try again.");
    }
  };

  const handleStatusChange = async (cycle: ProgrammeCycle, newStatus: QuarterStatus) => {
    setStatusError(null);
    try {
      await updateCycle({ id: cycle.id as string, status: newStatus }).unwrap();
    } catch (err: any) {
      setStatusError(err?.data?.error ?? "Could not update status.");
    }
  };

  const handleSeed = async (year: number) => {
    setShowSeed(false);
    await seedCycles({ year }).unwrap().catch(() => {});
  };

  const initialForm = useMemo<CycleFormProps>(() => {
    if (editTarget) return cycleToForm(editTarget);
    return { ...EMPTY_FORM, quarter: defaultQ, theme: QUARTER_THEMES[defaultQ].title, tagline: QUARTER_THEMES[defaultQ].tagline };
  }, [editTarget, defaultQ]);

  /* ─────────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-7 pb-12">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">Programme Management</p>
          <h1 className="text-2xl font-bold text-gray-900">Cycles & Quarters</h1>
          <p className="mt-1 text-sm text-gray-500">
            {canWrite
              ? "Configure Q1–Q4 programme cycles, set dates, and manage status transitions."
              : "View the programme cycle schedule and milestone dates."}
          </p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowSeed(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-xl hover:bg-purple-100 active:scale-95 transition-all"
            >
              <Zap size={15} /> Seed defaults
            </button>
            <button
              onClick={() => openCreate("Q1")}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:scale-95 transition-all shadow-sm"
            >
              <Plus size={16} /> New cycle
            </button>
          </div>
        )}
      </div>

      {/* STATUS ERROR */}
      {statusError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs text-red-600">
          <AlertCircle size={13} className="shrink-0" />{statusError}
          <button onClick={() => setStatusError(null)} className="ml-auto"><X size={13} /></button>
        </div>
      )}

      {/* READ-ONLY BANNER */}
      {!canWrite && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
          <Info size={13} className="shrink-0" />
          You have read-only access. Contact a Committee Chair or Admin to make changes.
        </div>
      )}

      {/* ACTIVE CYCLE HERO */}
      {!isLoading && activeCycle && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-700 to-purple-950 p-6 text-white shadow-md">
          <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full border border-white/10" />
          <div className="pointer-events-none absolute -right-3 -top-3 h-32 w-32 rounded-full border border-white/10" />
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-md uppercase tracking-wide">{activeCycle.quarter}</span>
                <span className="text-white/60 text-xs font-medium flex items-center gap-1"><Flame size={11} /> Currently Active</span>
              </div>
              <h2 className="text-lg font-bold leading-snug">{activeCycle.theme}</h2>
              <p className="text-white/60 text-xs mt-0.5">{activeCycle.tagline}</p>
              {activeCycle.pillars?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {activeCycle.pillars.map((p) => (
                    <span key={p} className="text-[10px] font-medium bg-white/15 px-2 py-0.5 rounded-full">{p}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="shrink-0 sm:text-right">
              <p className="text-white/60 text-xs mb-1 flex sm:justify-end items-center gap-1"><Calendar size={11} /> Event date</p>
              <p className="text-3xl font-bold tabular-nums">{activeCycle.daysToEvent ?? "—"}</p>
              <p className="text-white/50 text-xs">days remaining</p>
              <div className="mt-3 w-full sm:w-40">
                <div className="flex justify-between text-[11px] text-white/50 mb-1.5">
                  <span>Progress</span><span>{activeCycle.progressPercent}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div className="h-full rounded-full bg-white" style={{ width: `${activeCycle.progressPercent}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SUMMARY TILES */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(["locked", "upcoming", "active", "completed"] as QuarterStatus[]).map((s) => {
          const count = cycles.filter((c) => c.status === s).length;
          const meta  = STATUS_META[s];
          return (
            <div key={s} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4">
              <div className="flex items-center gap-1.5 mb-1 text-[11px] text-gray-400">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {meta.label}
              </div>
              {isLoading
                ? <div className="h-7 w-8 bg-gray-100 animate-pulse rounded-lg mt-1" />
                : <p className="text-2xl font-bold text-gray-800">{count}</p>
              }
            </div>
          );
        })}
      </div>

      {/* 4-QUARTER GRID */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => <Bone key={i} className="h-72" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {QUARTER_ORDER.map((q) => (
            <CycleCard
              key={q}
              quarter={q}
              cycle={cycleMap[q] ?? null}
              canWrite={canWrite}
              onEdit={openEdit}
              onCreate={openCreate}
              onStatus={handleStatusChange}
              isUpdating={isUpdating}
            />
          ))}
        </div>
      )}

      {/* ALL CYCLES TABLE — shows history when multiple years exist */}
      {cycles.length > 4 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">All cycles</p>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-100">
                    {["Quarter", "Year", "Theme", "Status", "Event date", "Days to event", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {cycles.map((c) => {
                    const sm = STATUS_META[c.status];
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md text-xs">{c.quarter}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">{c.year ?? "—"}</td>
                        <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{c.theme ?? "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${sm.colour}`}>
                            {sm.icon}{sm.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(c.eventDate)}</td>
                        <td className="px-4 py-3 text-xs font-medium text-purple-700">
                          {c.daysToEvent !== null && c.daysToEvent >= 0 ? `${c.daysToEvent}d` : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {canWrite && (
                            <button onClick={() => openEdit(c)} className="p-1 rounded text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors">
                              <Edit3 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* FORM SLIDE-IN */}
      {showForm && canWrite && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="flex-1 bg-black/40" onClick={() => setShowForm(false)} />
          <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editTarget ? `Edit cycle — ${editTarget.quarter}` : "Create programme cycle"}
                </h2>
                <p className="text-xs text-gray-400">
                  {editTarget ? editTarget.theme ?? "" : "Fill in the required fields."}
                </p>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <CycleForm
                initial={initialForm}
                isEditing={!!editTarget}
                isSaving={isCreating || isUpdating}
                apiError={formError}
                onSave={handleSave}
                onCancel={() => { setShowForm(false); setEditTarget(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* SEED MODAL */}
      {showSeed && canWrite && (
        <SeedModal
          isLoading={isSeeding}
          onConfirm={handleSeed}
          onCancel={() => setShowSeed(false)}
        />
      )}
    </div>
  );
};

export default AdminCycles;